import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import Compressor from 'compressorjs';

import { IconPhoto, IconX, IconInfoCircle, IconLoader } from '@tabler/icons';

import api from '../../../services/api';
import { useToast } from '../../../hooks/toast';

import BackHeader from '../../../components/BackHeader';
import {
  Row,
  Spin,
  Skeleton,
  NotFoundContainer,
} from '../../../components/Generic';
import { Container, Content, UploadButton, ImageCard } from './styles';

const NewProperty: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [fileLoading, setFileLoading] = useState(false);
  const [images, setImages] = useState<any>([]);

  const { id }: any = useParams();
  const { addToast } = useToast();

  useEffect(() => {
    getImages();
  }, []);

  const getImages = async () => {
    try {
      setLoading(true);

      const response = await api.get(`/imagens/`, {
        headers: {
          propriedade_id: id,
        },
      });

      setImages(response.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadImage = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target?.files?.[0];

      if (file) {
        setFileLoading(true);

        try {
          new Compressor(file, {
            quality: 0.6,
            maxHeight: 700,
            maxWidth: 700,
            success: async resultBlob => {
              const compressedFile = new File([resultBlob], file.name, {
                type: 'image/jpeg',
              });

              const data = new FormData();
              data.append('imagens', compressedFile);

              await api.patch(`/imagens/`, data, {
                headers: {
                  propriedade_id: id,
                },
              });

              addToast({
                type: 'success',
                title: 'Sucesso!',
                description: 'Imagem carregada',
              });

              getImages();
            },
            error(err) {
              console.log(err);

              addToast({
                type: 'error',
                title: 'Erro!',
                description: 'Erro ao carregar imagem.',
              });
            },
          });
        } catch (err) {
          addToast({
            type: 'error',
            title: 'Erro!',
            description:
              err.response.data.message || 'A imagem não foi carregada.',
          });
        } finally {
          setFileLoading(false);
        }
      }
    },
    [],
  );

  const deleteImage = useCallback(async (image_id: string) => {
    try {
      await api.delete(`/imagens/${image_id}`, {
        headers: {
          propriedade_id: id,
        },
      });

      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'Imagem deletada.',
      });

      getImages();
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response.data.message || 'Algo deu errado.',
      });
    }
  }, []);

  return (
    <Container>
      <BackHeader title="Imagens" />

      {loading ? (
        <Content>
          <Skeleton style={{ height: 60, width: '100%', marginBottom: 30 }} />
          <Skeleton style={{ height: 200, width: '100%', marginBottom: 10 }} />
          <Skeleton style={{ height: 200, width: '100%', marginBottom: 10 }} />
        </Content>
      ) : (
        <Content>
          <UploadButton isLoading={fileLoading}>
            <label htmlFor="image">
              {fileLoading ? (
                <IconLoader size={24} className="spin" />
              ) : (
                <IconPhoto size={24} />
              )}
              Adicionar imagem
              <input type="file" id="image" onChange={handleUploadImage} />
            </label>
          </UploadButton>

          <Row style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
            {images.length > 0 ? (
              images?.map((image: any, index: any) => (
                <ImageCard>
                  <button onClick={() => deleteImage(images[index].id)}>
                    <IconX size={15} />
                  </button>

                  <img src={image.image_url} alt="Imagem" />
                </ImageCard>
              ))
            ) : (
              <NotFoundContainer>
                <IconInfoCircle
                  size={25}
                  color="#999"
                  style={{ marginRight: 20 }}
                />

                <strong>Nenhuma imagem cadastrada.</strong>
              </NotFoundContainer>
            )}
          </Row>
        </Content>
      )}
    </Container>
  );
};

export default NewProperty;
